import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Store, select } from '@ngrx/store';
import { ActiveCartFacade, Cart } from '@spartacus/cart/base/root';
import { CheckoutStepService } from '@spartacus/checkout/base/components';
import { CheckoutReplenishmentFormService } from '@spartacus/checkout/scheduled-replenishment/components';
import { GlobalMessageService, RoutingService, StateWithProcess, WindowRef } from '@spartacus/core';
import { ORDER_TYPE, OrderFacade, ScheduledReplenishmentOrderFacade } from '@spartacus/order/root';
import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';
import { tap } from "rxjs/operators";
import { CustomRedsysPayment } from 'src/app/spartacus/custom/core/src/checkout/models/custom-checkout.model';
import { ScriptService } from '../../../../../../custom/services/script.service';
import { CustomValidateCartService } from '../../../../../../services/cart/validate-cart.service';
import { CustomCheckoutService } from '../../../../core/src/checkout/facade/custom-checkout.service';
import { CustomPaymentTypeEnum } from "../../../../model/cart.model";
import { CreditCard, StateWithCreditCards } from '../custom-credit-card/store/credit-card.state';
import { getSaveCreditCard } from '../custom-credit-card/store/selectors/credit-card.selectors';

declare global {
  interface Window {
    SequraFormInstance: any;
    SequraFormElement: any;
  }
}

@Component({
  selector: 'cx-place-order',
  templateUrl: './custom-place-order.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})

export class CustomPlaceOrderComponent implements OnInit, OnDestroy {
  @Input() typeSelected: string;
  @Output() loaderEvent = new EventEmitter<boolean>();
  private subscriptions: Subscription = new Subscription();
  currentOrderType: ORDER_TYPE;
  saveCard = null;
  daysOfWeekNotChecked$ = new BehaviorSubject<boolean>(false);
  selectedCreditCard: CreditCard;
  paymentTypeId: string;
  sequraIframe: boolean;

  cart: Cart = null;
  cart$: Observable<Cart> = this.activeCartService.getActive();

  redsysForm: UntypedFormGroup = this.fb.group({
    Ds_MerchantParameters: ['', Validators.requiredTrue],
    Ds_SignatureVersion: ['', Validators.requiredTrue],
    Ds_Signature: ['', Validators.requiredTrue],
  });

  constructor(
    protected checkoutService: CustomCheckoutService,
    protected orderFacade: OrderFacade,
    protected routingService: RoutingService,
    protected fb: UntypedFormBuilder,
    protected checkoutReplenishmentFormService: CheckoutReplenishmentFormService,
    protected scheduledReplenishmentOrderFacade: ScheduledReplenishmentOrderFacade,
    @Inject(PLATFORM_ID) private platformId: any,
    protected customValidateCartService?: CustomValidateCartService,
    protected creditCardStore?: Store<StateWithCreditCards | StateWithProcess<void>>,
    protected globalMessageService?: GlobalMessageService,
    protected checkoutStepService?: CheckoutStepService,
    protected script?: ScriptService,
    protected activeCartService?: ActiveCartFacade,
    protected winRef?: WindowRef
  ) {
  }

  ngOnInit(): void {
    this.subscriptions.add(this.cart$.pipe(tap(cart => this.cart = cart)).subscribe());
    this.subscriptions.add(this.creditCardStore.pipe(select(getSaveCreditCard)).subscribe(saveCard => this.saveCard = saveCard));

    this.subscriptions.add(
      this.checkoutReplenishmentFormService
        .getOrderType()
        .subscribe((orderType) => (this.currentOrderType = orderType))
    );

    if (isPlatformBrowser(this.platformId)) {
      const div = this.winRef.document.createElement('div');
      div.classList.add('sequra-payment');
      this.winRef.document.body.appendChild(div);
    }

    this.subscriptions.add(
      combineLatest([
        this.checkoutService.getRedsysPaymentLoading(),
        this.checkoutService.getRedsysPaymentSuccess(),
        this.checkoutService.getRedsysPaymentError(),
        this.checkoutService.getSequraPaymentLoading(),
        this.checkoutService.getSequraPaymentSuccess(),
        this.checkoutService.getSequraPaymentError(),
        this.checkoutService.getPaypalPaymentLoading(),
        this.checkoutService.getPaypalPaymentSuccess(),
        this.checkoutService.getPaypalPaymentError(),
        this.checkoutService.getPlaceOrderLoading(),
        this.checkoutService.getPlaceOrderSuccess(),
        this.checkoutService.getPlaceOrderError(),
      ]).subscribe(([
                      redsysLoading, redsysLoadingSuccess, redsysLoadingError,
                      sequraLoading, sequraLoadingSuccess, sequraLoadingError,
                      paypalLoading, paypalLoadingSuccess, paypalLoadingError,
                      loading, loadingSuccess, loadingError,
                    ]) => {
        if (redsysLoading || sequraLoading || paypalLoading || loading) {
          this.startLoaderComponent();
        } else {
          this.removeLoaderComponent();
        }
        // Activate the event when the payment button is clicked and the payment process is successfully initiated.
        if (redsysLoadingSuccess || sequraLoadingSuccess || paypalLoadingSuccess) {
          this.onPaymentLoadedSuccess();
        }
        if (redsysLoadingError || sequraLoadingError || paypalLoadingError || loadingError) {
          this.checkoutService.clearPlaceOrderState();
        }
        if (redsysLoadingSuccess && this.typeSelected === CustomPaymentTypeEnum.CARD_PAYMENT) {
          this.onRedsysLoadingSuccess();
        }
        if (sequraLoadingSuccess && this.typeSelected === CustomPaymentTypeEnum.SEQURA_PAYMENT_SP1 || this.typeSelected === CustomPaymentTypeEnum.SEQURA_PAYMENT_PP3) {
          this.onSequraLoadingSuccess();
        }
        if (paypalLoadingSuccess && this.typeSelected === CustomPaymentTypeEnum.PAYPAL_PAYMENT) {
          this.onPayPalLoadingSuccess(paypalLoadingSuccess);
        }
        if (loadingSuccess) {
          this.onSuccess();
        }
      }),
    );
  }

  submitForm(): void {

    if (this.currentOrderType === ORDER_TYPE.PLACE_ORDER) {
      switch (this.typeSelected) {
        case CustomPaymentTypeEnum.CARD_PAYMENT:
          this.checkoutService.initRedsysPayment(this.saveCard);
          break;
        case CustomPaymentTypeEnum.SEQURA_PAYMENT_SP1:
          this.checkoutService.initSequraPayment(this.typeSelected);
          break;
        case CustomPaymentTypeEnum.SEQURA_PAYMENT_PP3:
          this.checkoutService.initSequraPayment(this.typeSelected);
          break;
        case CustomPaymentTypeEnum.PAYPAL_PAYMENT:
          this.checkoutService.initPaypalPayment(this.saveCard);
          break;
        default:
          if (
            this.cart?.totalPriceWithTax?.value === 0 &&
            (
              this.cart.appliedOrderPromotions.length > 0 ||
              this.cart.appliedProductPromotions.length > 0 ||
              this.cart.appliedVouchers.length > 0
            )
          ) {
            this.orderFacade.placeOrder(true).subscribe({
              error: () => this.removeLoaderComponent(),
              next: () => this.onSuccess(),
            });
          }
          break;
      }
    }
  }

  startLoaderComponent(): void {
    this.loaderEvent.emit(true);
  }

  removeLoaderComponent(): void {
    this.loaderEvent.emit(false);
  }

  loadSequraLibrary(id: string, url: string): void {
    this.script.scripts.sequra.src = url;
    this.script.load('sequra').then(() => {
      window.SequraFormInstance.setElement(id)
      window.SequraFormInstance.show();
      this.removeLoaderComponent();
    }).catch(error => console.log(error));
  }

  onSequraLoadingSuccess(): void {
    this.checkoutService.getSequraPaymentState().subscribe(state => {
      if (state?.value?.product === this.typeSelected) {
        const sequraPayment: any = state.value;
        if (sequraPayment.hopFormActionUrl && isPlatformBrowser(this.platformId)) {
          this.winRef.document.querySelector('.sequra-payment').innerHTML = sequraPayment.hopFormActionUrl;
          this.loadSequraLibrary('sq-identification-' + sequraPayment.product, sequraPayment.library);
        }
      }
    }).unsubscribe();
  }

  onPayPalLoadingSuccess(isSuccess: boolean): void {
    this.checkoutService.getPaypalPaymentState().subscribe(state => {
      const paypal: any = state.value;
      if (paypal) {
        if (isPlatformBrowser(this.platformId)) {
          window.location.href = paypal.approvalUrl;
        }
      }
    }).unsubscribe();
  }

  onRedsysLoadingSuccess(): void {
    this.checkoutService.getRedsysPaymentState().subscribe(state => {
      const redsysPayment: CustomRedsysPayment = state.value;
      if (redsysPayment?.paymentSignature) {
        this.redsysForm.patchValue({
          Ds_MerchantParameters: redsysPayment.paymentParameters,
          Ds_SignatureVersion: redsysPayment.signatureVersion,
          Ds_Signature: redsysPayment.paymentSignature,
        });
        this.submitRedsysForm(redsysPayment.hopFormActionUrl);
      }
    }).unsubscribe();
  }

  onPaymentLoadedSuccess(): void {
    this.checkoutService.addPaymentInfoEvent();
  }

  submitRedsysForm(redsysUrl: string): void {
    const form = this.winRef.document.createElement('form');
    form.style.display = 'none';
    form.method = 'POST';
    form.action = redsysUrl;
    let input;
    for (const [key, value] of Object.entries(this.redsysForm.value)) {
      input = this.winRef.document.createElement('input');
      input.name = key;
      input.value = value;
      input.type = 'hidden';
      form.appendChild(input);
    }
    this.winRef.document.body.appendChild(form);
    form.submit();
  }

  onSuccess(): void {
    this.subscriptions.add(this.checkoutService.getOrderDetails().subscribe(() => {
        this.removeLoaderComponent();
        this.routingService.go({ cxRoute: 'orderConfirmation' });
      }
    ));
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
    this.customValidateCartService.clearSimulateCart();
    this.checkoutService.clearPlaceOrderState();
    this.removeLoaderComponent();
  }
}
